import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ApiError, Message, Thread, api } from "../api";

export default function ThreadDetail() {
  const { id } = useParams<{ id: string }>();
  const [thread, setThread] = useState<Thread | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [err, setErr] = useState<string | null>(null);

  const load = async () => {
    if (!id) return;
    const r = await api<{ thread: Thread; messages: Message[] }>(`/api/threads/${id}`);
    setThread(r.thread);
    setMessages(r.messages);
  };

  useEffect(() => {
    load().catch((e) => setErr(e.message));
  }, [id]);

  const approve = async (msgId: string) => {
    try {
      await api(`/api/drafts/${msgId}/send`, { method: "POST" });
      await load();
      alert("Draft enqueued for send.");
    } catch (e) {
      alert(e instanceof ApiError ? `${e.status} ${e.body}` : (e as Error).message);
    }
  };

  const saveDraft = async (msgId: string, subject: string, text: string) => {
    await api(`/api/drafts/${msgId}/edit`, {
      method: "POST",
      body: JSON.stringify({ subject, text }),
    });
    await load();
  };

  if (err) return <div className="card border-red-200 bg-red-50 p-4 text-sm text-red-700">{err}</div>;
  if (!thread) return <div className="text-sm text-ink-500">loading…</div>;

  return (
    <div className="space-y-4">
      <div>
        <Link to="/inbox" className="text-xs text-ink-500 hover:text-ink-700">← Inbox</Link>
        <div className="flex items-start justify-between mt-2 gap-3">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold tracking-tight">{thread.subject || "(no subject)"}</h1>
            <div className="text-xs text-ink-500 mt-1 flex flex-wrap gap-x-3 gap-y-1">
              {thread.contact_email && <span className="font-mono">{thread.contact_email}</span>}
              {thread.campaign_name && (
                <span>
                  campaign:{" "}
                  <Link to={`/campaigns/${thread.campaign_id}`} className="text-ink-700 hover:underline">
                    {thread.campaign_name}
                  </Link>
                </span>
              )}
            </div>
          </div>
          <div className="whitespace-nowrap">
            <span className="pill bg-ink-100 text-ink-700 capitalize">{thread.state.replace("_", " ")}</span>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {messages.map((m) => (
          <MessageCard
            key={m.id}
            m={m}
            onApprove={() => approve(m.id)}
            onSave={(subject, text) => saveDraft(m.id, subject, text)}
          />
        ))}
      </div>
    </div>
  );
}

function MessageCard({
  m,
  onApprove,
  onSave,
}: {
  m: Message;
  onApprove: () => void;
  onSave: (subject: string, text: string) => Promise<void>;
}) {
  const isInbound = m.direction === "in";
  let draftBody: { subject?: string; text?: string; html?: string | null } | null = null;
  if (m.ai_draft) {
    try { draftBody = JSON.parse(m.ai_draft); } catch { draftBody = null; }
  }

  const [editing, setEditing] = useState(false);
  const [subject, setSubject] = useState(draftBody?.subject ?? "");
  const [text, setText] = useState(draftBody?.text ?? "");
  const [saving, setSaving] = useState(false);

  const startEdit = () => {
    setSubject(draftBody?.subject ?? "");
    setText(draftBody?.text ?? "");
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await onSave(subject, text);
      setEditing(false);
    } catch (e) {
      alert(e instanceof ApiError ? `${e.status} ${e.body}` : (e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`card p-4 ${isInbound ? "border-l-4 border-l-blue-400" : "border-l-4 border-l-ink-700"}`}>
      <div className="flex items-center justify-between text-xs text-ink-500 mb-2">
        <div>
          <span className="pill bg-ink-100 text-ink-700 mr-2">{isInbound ? "FROM PROSPECT" : "FROM US"}</span>
          <span className="font-mono">{m.from_addr}</span> <span className="text-ink-400">→</span> <span className="font-mono">{m.to_addr}</span>
        </div>
        <div>{new Date(m.created_at * 1000).toLocaleString()}</div>
      </div>
      <div className="text-sm font-medium mb-2">{m.subject}</div>
      <pre className="whitespace-pre-wrap font-sans text-sm text-ink-700">{m.text_body}</pre>

      {isInbound && m.classification_label && (
        <div className="mt-3 flex items-center gap-2 text-xs">
          <span className="pill bg-purple-100 text-purple-800">{m.classification_label}</span>
          <span className="text-ink-500">conf {m.classification_confidence?.toFixed(2)}</span>
          {m.ai_action && <span className="text-ink-500">· action: {m.ai_action}</span>}
        </div>
      )}

      {isInbound && draftBody?.text && (
        <div className="mt-4 rounded-md border border-ink-200 bg-ink-50 p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-wide text-ink-500">AI draft reply</div>
            {m.ai_action !== "sent" && (
              <div className="flex items-center gap-2">
                {editing ? (
                  <>
                    <button
                      onClick={save}
                      disabled={saving}
                      className="btn-primary text-xs px-2 py-1 disabled:opacity-50"
                    >
                      {saving ? "Saving…" : "Save"}
                    </button>
                    <button
                      onClick={() => setEditing(false)}
                      disabled={saving}
                      className="btn-secondary text-xs px-2 py-1 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button onClick={startEdit} className="btn-secondary text-xs px-2 py-1">
                      Edit
                    </button>
                    <button onClick={onApprove} className="btn-primary text-xs px-2 py-1">
                      Approve & send
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
          {editing ? (
            <div className="space-y-2">
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Subject"
                className="input w-full text-sm"
              />
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={Math.max(6, text.split("\n").length + 1)}
                className="input w-full font-sans text-sm"
              />
            </div>
          ) : (
            <>
              {draftBody.subject && <div className="text-sm font-medium mb-1">{draftBody.subject}</div>}
              <pre className="whitespace-pre-wrap font-sans text-sm text-ink-700">{draftBody.text}</pre>
            </>
          )}
        </div>
      )}
    </div>
  );
}
